import {type FormEvent, useState} from "react";
import {BaseAlert, BaseButton, BaseCard, BaseInput} from "@/components";
import {useWallet} from "@/pages/private/Wallet/useWallet";
import {type TransferFormInput, transferSchema} from "./transfer.schema";
import {useTransfer} from "./useTransfer";

type FieldErrors = Partial<Record<keyof TransferFormInput, string>>;

const INITIAL_FORM: TransferFormInput = {
  receiver_id: "",
  amount: "" as unknown as number,
};

export interface TransferPageProps {
  onBack?: () => void;
  onSuccess?: () => void;
}

const TransferPage = ({onBack, onSuccess}: TransferPageProps) => {
  const {wallet} = useWallet();
  const mutation = useTransfer();

  const [form, setForm] = useState<TransferFormInput>(INITIAL_FORM);
  const [errors, setErrors] = useState<FieldErrors>({});

  const availableBalance = wallet?.defaultAccount?.balance ?? 0;
  const currency = wallet?.defaultAccount?.currency ?? "VES";

  const updateField = <K extends keyof TransferFormInput>(key: K, value: TransferFormInput[K]) => {
    setForm(prev => ({...prev, [key]: value}));
    setErrors(prev => ({...prev, [key]: undefined}));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = transferSchema.safeParse(form);
    if (!parsed.success) {
      const fieldErrors: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof TransferFormInput | undefined;
        if (key && !fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    if (parsed.data.amount > availableBalance) {
      setErrors({amount: "Monto superior a tu saldo disponible"});
      return;
    }
    mutation.mutate(parsed.data);
  };

  const handleReset = () => {
    setForm(INITIAL_FORM);
    setErrors({});
    mutation.reset();
  };

  if (mutation.isSuccess) {
    return (
      <section className="mx-auto flex w-full max-w-lg flex-col gap-4 px-4 py-8">
        <BaseAlert
          variant="success"
          title="Transferencia enviada"
        >
          <p className="mb-3">Tu transferencia fue procesada correctamente.</p>
          <div className="flex flex-wrap gap-2">
            <BaseButton
              variant="primary"
              size="sm"
              onClick={handleReset}
            >
              Nueva transferencia
            </BaseButton>
            {onSuccess ? (
              <BaseButton
                variant="secondary"
                size="sm"
                onClick={onSuccess}
              >
                Volver a la billetera
              </BaseButton>
            ) : null}
          </div>
        </BaseAlert>
      </section>
    );
  }

  return (
    <section className="mx-auto flex w-full max-w-lg flex-col gap-6 px-4 py-8">
      <header className="flex flex-col gap-2">
        <h1 className="text-[var(--font-h1)] font-bold text-[var(--color-text)]">Transferir saldo</h1>
        <p className="text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">
          Envía saldo a otro usuario. Saldo disponible:{" "}
          <span className="font-semibold text-[var(--color-text)]">
            {availableBalance.toFixed(2)} {currency}
          </span>
        </p>
      </header>

      {mutation.isError ? (
        <BaseAlert
          variant="danger"
          title="No pudimos enviar tu transferencia"
        >
          <p>{mutation.error?.message ?? "Inténtalo nuevamente."}</p>
        </BaseAlert>
      ) : null}

      <BaseCard
        variant="surface"
        padding="lg"
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={handleSubmit}
          noValidate
        >
          <BaseInput
            label="Destinatario"
            hint="ID, email o teléfono del usuario destino"
            autoComplete="off"
            placeholder="usuario@correo.com"
            value={form.receiver_id}
            onChange={e => updateField("receiver_id", e.target.value)}
            error={errors.receiver_id}
            disabled={mutation.isPending}
          />

          <BaseInput
            label={`Monto (${currency})`}
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            placeholder="0.00"
            value={String(form.amount ?? "")}
            onChange={e => updateField("amount", e.target.value as unknown as number)}
            error={errors.amount}
            disabled={mutation.isPending}
          />

          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <BaseButton
              type="submit"
              variant="primary"
              disabled={mutation.isPending}
              fullWidth
            >
              {mutation.isPending ? "Enviando..." : "Enviar transferencia"}
            </BaseButton>
            {onBack ? (
              <BaseButton
                type="button"
                variant="ghost"
                onClick={onBack}
                disabled={mutation.isPending}
              >
                Cancelar
              </BaseButton>
            ) : null}
          </div>
        </form>
      </BaseCard>
    </section>
  );
};

export default TransferPage;
